import VehicleImage from '../components/VehicleImage';
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { vehicleApi, categoryApi, qrApi, supplierApi, formatApiError } from '../services/api';
import { 
  Car, Plus, MagnifyingGlass, Funnel, PencilSimple, 
  Trash, GasPump, Gear, Users as SeatsIcon, Image, ChartLineUp
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import VehicleRevenueDialog from '../components/VehicleRevenueDialog';

const FUEL_TYPES = ['Benzin', 'Diesel', 'Elektro', 'Hybrid'];
const TRANSMISSIONS = ['Automatik', 'Manuell'];
const STATUSES = ['available', 'rented', 'maintenance', 'blocked'];

const VEHICLE_TYPES = [
  { value: 'pkw', label: 'PKW' },
  { value: 'transporter', label: 'Transporter' },
  { value: 'lkw', label: 'LKW' },
  { value: 'bus', label: 'Bus' },
  { value: 'wohnmobil', label: 'Wohnmobil' },
  { value: 'anhaenger', label: 'Anhänger' },
  { value: 'motorrad', label: 'Motorrad' },
  { value: 'sonstige', label: 'Sonstige' },
];
const VEHICLE_TYPE_LABELS = Object.fromEntries(VEHICLE_TYPES.map((t) => [t.value, t.label]));

const OWNERSHIP_TYPES = [
  { value: 'eigen',      label: 'Eigene' },
  { value: 'lieferant',  label: 'Lieferant / Leasing' },
  { value: 'kunden',     label: 'Kundenüberlassung' },
];
const OWNERSHIP_LABELS = Object.fromEntries(OWNERSHIP_TYPES.map((t) => [t.value, t.label]));
const OWNERSHIP_BADGE = {
  eigen:     { bg: 'bg-[#009951]', text: 'text-white', short: 'EIGEN' },
  lieferant: { bg: 'bg-[#FFB800]', text: 'text-[#111418]', short: 'LIEFERANT' },
  kunden:    { bg: 'bg-[#2E62FF]', text: 'text-white', short: 'KUNDEN' },
};

const STATUS_LABELS = {
  available: 'Verfügbar',
  rented: 'Vermietet',
  maintenance: 'Wartung',
  blocked: 'Blockiert'
};

const Vehicles = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterOwnership, setFilterOwnership] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    license_plate: '',
    year: new Date().getFullYear(),
    color: '',
    category: '',
    category_id: '',
    vehicle_type: 'pkw',
    ownership_type: 'eigen',
    supplier_id: '',
    supplier_share_pct: '',
    status: 'available',
    daily_rate: '',
    weekend_rate: '',
    weekly_rate: '',
    monthly_rate: '',
    fuel_type: 'Benzin',
    transmission: 'Automatik',
    seats: 5,
    default_deposit: 0,
    insurance_deductible: 0,
    included_km_per_day: '',
    extra_km_rate: '',
    // Stammdaten
    vin: '',
    first_registration_date: '',
    current_mileage: '',
    warranty_until: '',
    // TÜV
    tuev_date: '',
    tuev_inspector: '',
    // Inspektion
    next_inspection_date: '',
    // Versicherung
    insurance_company: '',
    insurance_policy_number: '',
    // Ölwechsel
    oil_change_interval_km: '',
    oil_change_interval_months: '',
    last_oil_change_date: '',
    last_oil_change_mileage: '',
    // Service
    service_interval_km: '',
    last_service_date: '',
    last_service_mileage: '',
    // Reifen
    last_tire_change_summer_date: '',
    last_tire_change_winter_date: '',
    notes: ''
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [revenueVehicle, setRevenueVehicle] = useState(null);

  useEffect(() => {
    loadVehicles();
    loadCategories();
    loadSuppliers();
  }, [filterStatus]);

  // Deep-link edit: /vehicles?edit=<vehicleId>  →  öffnet direkt den Bearbeiten-Dialog
  // (kommt z.B. vom Bearbeiten-Button auf der Fahrzeug-Detailseite)
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && vehicles.length > 0 && !showAddModal) {
      const v = vehicles.find((x) => x.id === editId);
      if (v) {
        handleEdit(v);
        // Remove the param so a page-refresh does not re-open the dialog
        const next = new URLSearchParams(searchParams);
        next.delete('edit');
        setSearchParams(next, { replace: true });
      }
    }
    // Depends on vehicles+searchParams+showAddModal; handleEdit is stable enough for this deep-link.
  }, [vehicles, searchParams]);

  const loadCategories = async () => {
    try {
      const res = await categoryApi.getAll();
      setCategories(res.data);
    } catch (_) { /* silent */ }
  };

  const loadSuppliers = async () => {
    try {
      const res = await supplierApi.getAll(true);
      setSuppliers(res.data || []);
    } catch (_) { /* silent */ }
  };

  const applyCategoryDefaults = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    if (!cat) return;
    setFormData(prev => ({
      ...prev,
      category_id: categoryId,
      category: cat.name,
      daily_rate: cat.daily_rate ?? prev.daily_rate,
      weekend_rate: cat.weekend_rate ?? '',
      weekly_rate: cat.weekly_rate ?? '',
      monthly_rate: cat.monthly_rate ?? '',
      default_deposit: cat.default_deposit ?? prev.default_deposit,
      insurance_deductible: cat.insurance_deductible ?? prev.insurance_deductible,
    }));
    toast.success(`Preise aus Kategorie "${cat.name}" übernommen`);
  };

  const loadVehicles = async () => {
    try {
      const params = {};
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      const response = await vehicleApi.getAll(params);
      setVehicles(response.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);
      const numOrNull = (v) => (v === '' || v === null ? null : parseFloat(v));
      const intOrNull = (v) => (v === '' || v === null ? null : parseInt(v));
      const strOrNull = (v) => (v === '' || v === null || v === undefined ? null : v);
      const payload = {
        ...formData,
        daily_rate: parseFloat(formData.daily_rate),
        weekend_rate: numOrNull(formData.weekend_rate),
        weekly_rate: numOrNull(formData.weekly_rate),
        monthly_rate: numOrNull(formData.monthly_rate),
        included_km_per_day: intOrNull(formData.included_km_per_day),
        extra_km_rate: numOrNull(formData.extra_km_rate),
        category_id: formData.category_id || null,
        vin: strOrNull(formData.vin),
        first_registration_date: strOrNull(formData.first_registration_date),
        current_mileage: intOrNull(formData.current_mileage),
        warranty_until: strOrNull(formData.warranty_until),
        tuev_date: strOrNull(formData.tuev_date),
        tuev_inspector: strOrNull(formData.tuev_inspector),
        next_inspection_date: strOrNull(formData.next_inspection_date),
        insurance_company: strOrNull(formData.insurance_company),
        insurance_policy_number: strOrNull(formData.insurance_policy_number),
        oil_change_interval_km: intOrNull(formData.oil_change_interval_km),
        oil_change_interval_months: intOrNull(formData.oil_change_interval_months),
        last_oil_change_date: strOrNull(formData.last_oil_change_date),
        last_oil_change_mileage: intOrNull(formData.last_oil_change_mileage),
        service_interval_km: intOrNull(formData.service_interval_km),
        last_service_date: strOrNull(formData.last_service_date),
        last_service_mileage: intOrNull(formData.last_service_mileage),
        last_tire_change_summer_date: strOrNull(formData.last_tire_change_summer_date),
        last_tire_change_winter_date: strOrNull(formData.last_tire_change_winter_date),
        supplier_id: formData.ownership_type === 'lieferant' ? (formData.supplier_id || null) : null,
        supplier_share_pct: formData.ownership_type === 'lieferant' ? numOrNull(formData.supplier_share_pct) : null,
      };
      let vehicleId;
      if (editingVehicle) {
        await vehicleApi.update(editingVehicle.id, payload);
        vehicleId = editingVehicle.id;
      } else {
        const res = await vehicleApi.create(payload);
        vehicleId = res.data.id;
      }
      // Upload image if selected
      if (imageFile && vehicleId) {
        try {
          await vehicleApi.uploadImage(vehicleId, imageFile);
        } catch (uploadErr) {
          toast.error('Bildupload fehlgeschlagen: ' + formatApiError(uploadErr));
        }
      }
      toast.success(editingVehicle ? 'Fahrzeug aktualisiert' : 'Fahrzeug hinzugefügt');
      setShowAddModal(false);
      setEditingVehicle(null);
      resetForm();
      loadVehicles();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setUploading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Bild ist zu groß (max. 10 MB)');
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte eine Bilddatei auswählen');
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const clearImageSelection = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Fahrzeug wirklich löschen?')) return;
    try {
      await vehicleApi.delete(id);
      toast.success('Fahrzeug gelöscht');
      loadVehicles();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    clearImageSelection();
    setFormData({
      brand: vehicle.brand,
      model: vehicle.model,
      license_plate: vehicle.license_plate,
      year: vehicle.year,
      color: vehicle.color,
      category: vehicle.category,
      category_id: vehicle.category_id || '',
      vehicle_type: vehicle.vehicle_type || 'pkw',
      ownership_type: vehicle.ownership_type || 'eigen',
      supplier_id: vehicle.supplier_id || '',
      supplier_share_pct: vehicle.supplier_share_pct != null ? String(vehicle.supplier_share_pct) : '',
      status: vehicle.status || 'available',
      daily_rate: vehicle.daily_rate,
      weekend_rate: vehicle.weekend_rate ?? '',
      weekly_rate: vehicle.weekly_rate ?? '',
      monthly_rate: vehicle.monthly_rate ?? '',
      fuel_type: vehicle.fuel_type,
      transmission: vehicle.transmission,
      seats: vehicle.seats,
      default_deposit: vehicle.default_deposit || 0,
      insurance_deductible: vehicle.insurance_deductible || 0,
      included_km_per_day: vehicle.included_km_per_day ?? '',
      extra_km_rate: vehicle.extra_km_rate ?? '',
      vin: vehicle.vin || '',
      first_registration_date: vehicle.first_registration_date || '',
      current_mileage: vehicle.current_mileage ?? '',
      warranty_until: vehicle.warranty_until || '',
      tuev_date: vehicle.tuev_date || '',
      tuev_inspector: vehicle.tuev_inspector || '',
      next_inspection_date: vehicle.next_inspection_date || '',
      insurance_company: vehicle.insurance_company || '',
      insurance_policy_number: vehicle.insurance_policy_number || '',
      oil_change_interval_km: vehicle.oil_change_interval_km ?? '',
      oil_change_interval_months: vehicle.oil_change_interval_months ?? '',
      last_oil_change_date: vehicle.last_oil_change_date || '',
      last_oil_change_mileage: vehicle.last_oil_change_mileage ?? '',
      service_interval_km: vehicle.service_interval_km ?? '',
      last_service_date: vehicle.last_service_date || '',
      last_service_mileage: vehicle.last_service_mileage ?? '',
      last_tire_change_summer_date: vehicle.last_tire_change_summer_date || '',
      last_tire_change_winter_date: vehicle.last_tire_change_winter_date || '',
      notes: vehicle.notes || ''
    });
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({
      brand: '',
      model: '',
      license_plate: '',
      year: new Date().getFullYear(),
      color: '',
      category: '',
      category_id: '',
      vehicle_type: 'pkw',
      status: 'available',
      daily_rate: '',
      weekend_rate: '',
      weekly_rate: '',
      monthly_rate: '',
      fuel_type: 'Benzin',
      transmission: 'Automatik',
      seats: 5,
      default_deposit: 0,
      insurance_deductible: 0,
      included_km_per_day: '',
      extra_km_rate: '',
      vin: '',
      first_registration_date: '',
      current_mileage: '',
      warranty_until: '',
      tuev_date: '',
      tuev_inspector: '',
      next_inspection_date: '',
      insurance_company: '',
      insurance_policy_number: '',
      oil_change_interval_km: '',
      oil_change_interval_months: '',
      last_oil_change_date: '',
      last_oil_change_mileage: '',
      service_interval_km: '',
      last_service_date: '',
      last_service_mileage: '',
      last_tire_change_summer_date: '',
      last_tire_change_winter_date: '',
      notes: ''
    });
    clearImageSelection();
  };

  const filteredVehicles = vehicles.filter(v => {
    if (filterType !== 'all' && (v.vehicle_type || 'pkw') !== filterType) return false;
    if (filterOwnership !== 'all' && (v.ownership_type || 'eigen') !== filterOwnership) return false;
    if (!search) return true;
    const term = search.toLowerCase();
    return (
      v.brand.toLowerCase().includes(term) ||
      v.model.toLowerCase().includes(term) ||
      v.license_plate.toLowerCase().includes(term)
    );
  });

  const getStatusBadge = (status) => {
    const styles = {
      available: 'badge-success',
      rented: 'badge-error',
      maintenance: 'badge-warning',
      blocked: 'badge-neutral'
    };
    return <span className={`badge ${styles[status] || 'badge-neutral'}`}>{STATUS_LABELS[status] || status}</span>;
  };

  return (
    <div className="p-6 space-y-6" data-testid="vehicles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#111418]">Fahrzeuge</h1>
          <p className="text-[#525866] mt-1">{vehicles.length} {vehicles.length === 1 ? 'Fahrzeug' : 'Fahrzeuge'}</p>
        </div>
        <a
          href={qrApi.allLabelsPdfUrl('sticker')}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold border border-[#E2E4E9] rounded-sm hover:bg-[#FAFAFB] mr-2"
          data-testid="bulk-qr-labels-btn"
        >
          QR-Etiketten drucken
        </a>
        <Button
          onClick={() => { resetForm(); setEditingVehicle(null); setShowAddModal(true); }}
          className="btn-primary"
          disabled={vehicles.length >= 20}
          data-testid="add-vehicle-btn"
        >
          <Plus weight="bold" className="mr-2" size={18} />
          Fahrzeug hinzufügen
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A94A6]" size={20} />
          <Input
            type="text"
            placeholder="Suchen nach Marke, Modell, Kennzeichen..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 input-swiss"
            data-testid="vehicle-search-input"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full sm:w-48 input-swiss" data-testid="vehicle-status-filter">
            <Funnel className="mr-2" size={16} />
            <SelectValue placeholder="Alle Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            {STATUSES.map(s => (
              <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-full sm:w-48 input-swiss" data-testid="vehicle-type-filter">
            <Funnel className="mr-2" size={16} />
            <SelectValue placeholder="Alle Typen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Typen</SelectItem>
            {VEHICLE_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterOwnership} onValueChange={setFilterOwnership}>
          <SelectTrigger className="w-full sm:w-56 input-swiss" data-testid="vehicle-ownership-filter">
            <Funnel className="mr-2" size={16} />
            <SelectValue placeholder="Alle Eigentumsarten" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Eigentumsarten</SelectItem>
            {OWNERSHIP_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Vehicle Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="card-swiss h-64 skeleton" />
          ))}
        </div>
      ) : filteredVehicles.length === 0 ? (
        <div className="card-swiss p-12 text-center">
          <Car weight="thin" size={64} className="mx-auto text-[#8A94A6] mb-4" />
          <p className="text-[#525866]">Keine Fahrzeuge gefunden</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredVehicles.map((vehicle) => (
            <div 
              key={vehicle.id} 
              className="card-swiss overflow-hidden transition-all duration-200"
              data-testid={`vehicle-card-${vehicle.id}`}
            >
              {/* Image (clickable → details) */}
              <Link
                to={`/vehicles/${vehicle.id}`}
                className="block aspect-video bg-[#F7F8FA] relative cursor-pointer group"
                data-testid={`open-vehicle-${vehicle.id}`}
                aria-label={`Details für ${vehicle.brand} ${vehicle.model}`}
              >
                {vehicle.image_url ? (
                  <VehicleImage path={vehicle.image_url}
                    alt={`${vehicle.brand} ${vehicle.model}`}
                    className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Car weight="thin" size={48} className="text-[#8A94A6]" />
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  {getStatusBadge(vehicle.status)}
                </div>
                {vehicle.vehicle_type && (
                  <div className="absolute top-2 left-2 flex flex-col gap-1 items-start">
                    <span className="text-xs font-bold px-2 py-0.5 bg-[#111418]/85 text-white rounded-sm uppercase tracking-wide" data-testid={`vehicle-type-badge-${vehicle.id}`}>
                      {VEHICLE_TYPE_LABELS[vehicle.vehicle_type] || vehicle.vehicle_type}
                    </span>
                    {vehicle.ownership_type && vehicle.ownership_type !== 'eigen' && (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm uppercase tracking-wide ${OWNERSHIP_BADGE[vehicle.ownership_type]?.bg || 'bg-[#525866]'} ${OWNERSHIP_BADGE[vehicle.ownership_type]?.text || 'text-white'}`} data-testid={`vehicle-ownership-badge-${vehicle.id}`}>
                        {OWNERSHIP_BADGE[vehicle.ownership_type]?.short || vehicle.ownership_type}
                      </span>
                    )}
                  </div>
                )}
              </Link>
              
              {/* Info */}
              <div className="p-4">
                <Link to={`/vehicles/${vehicle.id}`} className="block group">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-bold text-[#111418] group-hover:text-[#E60000] transition-colors">{vehicle.brand} {vehicle.model}</h3>
                      <p className="text-sm text-[#525866] flex items-center gap-2 flex-wrap">
                        <span>{vehicle.license_plate}</span>
                        {vehicle.vehicle_number && (
                          <span className="text-xs font-mono px-1.5 py-0.5 bg-[#F1F2F4] rounded text-[#525866]">
                            {vehicle.vehicle_number}
                          </span>
                        )}
                      </p>
                    </div>
                    <p className="text-lg font-bold text-[#E60000]">€{vehicle.daily_rate}/Tag</p>
                  </div>
                </Link>
                
                <div className="flex items-center gap-4 text-xs text-[#8A94A6] mb-4">
                  <span className="flex items-center gap-1">
                    <GasPump weight="bold" size={14} />
                    {vehicle.fuel_type}
                  </span>
                  <span className="flex items-center gap-1">
                    <Gear weight="bold" size={14} />
                    {vehicle.transmission}
                  </span>
                  <span className="flex items-center gap-1">
                    <SeatsIcon weight="bold" size={14} />
                    {vehicle.seats}
                  </span>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(vehicle)}
                    className="flex-1"
                    data-testid={`edit-vehicle-${vehicle.id}`}
                  >
                    <PencilSimple weight="bold" size={14} className="mr-1" />
                    Bearbeiten
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRevenueVehicle(vehicle)}
                    title="Umsatz pro Monat anzeigen"
                    data-testid={`vehicle-revenue-${vehicle.id}`}
                  >
                    <ChartLineUp weight="bold" size={14} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(vehicle.id)}
                    className="text-[#E60000] hover:bg-red-50"
                    data-testid={`delete-vehicle-${vehicle.id}`}
                  >
                    <Trash weight="bold" size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingVehicle ? 'Fahrzeug bearbeiten' : 'Neues Fahrzeug'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Fahrzeugbild Upload */}
            <div>
              <Label className="overline mb-2 block">Fahrzeugbild</Label>
              <div className="flex items-start gap-4">
                <div className="w-40 h-28 rounded-sm border border-[#E2E4E9] bg-[#F7F8FA] flex items-center justify-center overflow-hidden flex-shrink-0">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Vorschau" className="w-full h-full object-cover" />
                  ) : editingVehicle?.image_url ? (
                    <VehicleImage path={editingVehicle.image_url}
                      alt="Aktuell"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Image size={32} className="text-[#8A94A6]" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-[#E60000] file:text-white file:font-semibold file:cursor-pointer hover:file:bg-[#B30000]"
                    data-testid="vehicle-image-input"
                  />
                  <p className="text-xs text-[#8A94A6]">
                    JPG, PNG oder WebP – max. 10 MB. {editingVehicle?.image_url && !imagePreview && 'Aktuelles Bild bleibt erhalten, wenn keines ausgewählt wird.'}
                  </p>
                  {imagePreview && (
                    <button
                      type="button"
                      onClick={clearImageSelection}
                      className="text-xs text-red-600 hover:underline"
                      data-testid="clear-image-btn"
                    >
                      Auswahl entfernen
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline">Marke</Label>
                <Input
                  value={formData.brand}
                  onChange={(e) => setFormData({...formData, brand: e.target.value})}
                  className="input-swiss"
                  required
                  data-testid="vehicle-brand-input"
                />
              </div>
              <div>
                <Label className="overline">Modell</Label>
                <Input
                  value={formData.model}
                  onChange={(e) => setFormData({...formData, model: e.target.value})}
                  className="input-swiss"
                  required
                  data-testid="vehicle-model-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline">Kennzeichen</Label>
                <Input
                  value={formData.license_plate}
                  onChange={(e) => setFormData({...formData, license_plate: e.target.value.toUpperCase()})}
                  className="input-swiss"
                  required
                  data-testid="vehicle-plate-input"
                />
              </div>
              <div>
                <Label className="overline">Baujahr</Label>
                <Input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({...formData, year: parseInt(e.target.value)})}
                  className="input-swiss"
                  min={2000}
                  max={new Date().getFullYear() + 1}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="overline">Farbe</Label>
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData({...formData, color: e.target.value})}
                  className="input-swiss"
                  required
                />
              </div>
              <div>
                <Label className="overline">Fahrzeugtyp</Label>
                <Select
                  value={formData.vehicle_type || 'pkw'}
                  onValueChange={(v) => setFormData({...formData, vehicle_type: v})}
                >
                  <SelectTrigger className="input-swiss" data-testid="vehicle-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VEHICLE_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value} data-testid={`vehicle-type-option-${t.value}`}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
              <div>
                <Label className="overline">Status</Label>
                <Select
                  value={formData.status || 'available'}
                  onValueChange={(v) => setFormData({...formData, status: v})}
                >
                  <SelectTrigger className="input-swiss" data-testid="vehicle-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`vehicle-status-option-${s}`}>
                        {STATUS_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#8A94A6] mt-1">Wird bei Check-In/Out automatisch gesetzt. „Blockiert&ldquo; verhindert Buchungen.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
              <div>
                <Label className="overline">Eigentumsart</Label>
                <Select
                  value={formData.ownership_type || 'eigen'}
                  onValueChange={(v) => setFormData({...formData, ownership_type: v})}
                >
                  <SelectTrigger className="input-swiss" data-testid="vehicle-ownership-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OWNERSHIP_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value} data-testid={`vehicle-ownership-option-${t.value}`}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#8A94A6] mt-1">Eigene = eigene Flotte · Lieferant = geleast/fremd · Kundenüberlassung = vom Kunden zur Vermietung überlassen.</p>
              </div>
            </div>

            {/* Lieferant-Zuordnung (nur wenn ownership_type == 'lieferant') */}
            {formData.ownership_type === 'lieferant' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-[#E2E4E9]">
                <div>
                  <Label className="overline">Lieferant</Label>
                  <Select
                    value={formData.supplier_id || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, supplier_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger className="input-swiss" data-testid="vehicle-supplier-select">
                      <SelectValue placeholder="— kein Lieferant ausgewählt —" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="none">— kein Lieferant ausgewählt —</SelectItem>
                      {suppliers.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name} · {s.default_share_pct}% Standard-Anteil
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#8A94A6] mt-1">
                    Noch nicht in der Liste? Unter <em>Lieferanten-Abrechnung</em> im Menü einen neuen anlegen.
                  </p>
                </div>
                <div>
                  <Label className="overline">Anteil % (Fahrzeug-Override)</Label>
                  <Input
                    type="number" min={0} max={100} step={0.5}
                    value={formData.supplier_share_pct}
                    onChange={(e) => setFormData({ ...formData, supplier_share_pct: e.target.value })}
                    placeholder="leer = Standard vom Lieferant"
                    className="input-swiss"
                    data-testid="vehicle-supplier-share-input"
                  />
                  <p className="text-xs text-[#8A94A6] mt-1">
                    Leer lassen für den Lieferanten-Standard, sonst individueller Prozentsatz für dieses Fahrzeug.
                  </p>
                </div>
              </div>
            )}

            <div className="pt-2 border-t border-[#E2E4E9]">
              <Label className="overline block mb-2">Kategorie <span className="text-xs text-gray-400 font-normal">(optional — füllt Preise als Vorlage)</span></Label>
              <Select
                value={formData.category_id || 'none'}
                onValueChange={(v) => v === 'none' ? setFormData({...formData, category_id: '', category: ''}) : applyCategoryDefaults(v)}
              >
                <SelectTrigger className="input-swiss" data-testid="vehicle-category-select">
                  <SelectValue placeholder="Keine Kategorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keine Kategorie</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} — € {Number(c.daily_rate).toFixed(2)}/Tag
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="pt-2 border-t border-[#E2E4E9]">
              <Label className="overline block mb-3">Tarife (brutto, inkl. MwSt.)</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Tagespreis (€) *</Label>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={formData.daily_rate}
                    onChange={(e) => setFormData({...formData, daily_rate: e.target.value})}
                    className="input-swiss"
                    required
                    placeholder="z. B. 279"
                    data-testid="vehicle-rate-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Wochenendpreis (€)</Label>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={formData.weekend_rate}
                    onChange={(e) => setFormData({...formData, weekend_rate: e.target.value})}
                    className="input-swiss"
                    placeholder="Pauschale Fr–So"
                    data-testid="vehicle-weekend-rate"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Wochenpreis (€)</Label>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={formData.weekly_rate}
                    onChange={(e) => setFormData({...formData, weekly_rate: e.target.value})}
                    className="input-swiss"
                    placeholder="7-Tage Pauschale"
                    data-testid="vehicle-weekly-rate"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Monatspreis (€)</Label>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={formData.monthly_rate}
                    onChange={(e) => setFormData({...formData, monthly_rate: e.target.value})}
                    className="input-swiss"
                    placeholder="28-Tage Pauschale"
                    data-testid="vehicle-monthly-rate"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="overline">Kraftstoff</Label>
                <Select 
                  value={formData.fuel_type} 
                  onValueChange={(v) => setFormData({...formData, fuel_type: v})}
                >
                  <SelectTrigger className="input-swiss">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FUEL_TYPES.map(f => (
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline">Getriebe</Label>
                <Select 
                  value={formData.transmission} 
                  onValueChange={(v) => setFormData({...formData, transmission: v})}
                >
                  <SelectTrigger className="input-swiss">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRANSMISSIONS.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline">Sitze</Label>
                <Input
                  type="number"
                  value={formData.seats}
                  onChange={(e) => setFormData({...formData, seats: parseInt(e.target.value)})}
                  className="input-swiss"
                  min={2}
                  max={9}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline">Kaution (€)</Label>
                <Input
                  type="number"
                  value={formData.default_deposit}
                  onChange={(e) => setFormData({...formData, default_deposit: parseFloat(e.target.value) || 0})}
                  className="input-swiss"
                  min={0}
                  step={0.01}
                  placeholder="z. B. 2000"
                  data-testid="vehicle-deposit-input"
                />
              </div>
              <div>
                <Label className="overline">Selbstbehalt Vollkasko (€)</Label>
                <Input
                  type="number"
                  value={formData.insurance_deductible}
                  onChange={(e) => setFormData({...formData, insurance_deductible: parseFloat(e.target.value) || 0})}
                  className="input-swiss"
                  min={0}
                  step={0.01}
                  placeholder="z. B. 2500"
                  data-testid="vehicle-deductible-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="overline">Inklusivkilometer / Tag</Label>
                <Input
                  type="number"
                  value={formData.included_km_per_day}
                  onChange={(e) => setFormData({...formData, included_km_per_day: e.target.value})}
                  className="input-swiss"
                  min={0}
                  step={1}
                  placeholder="z. B. 250 (leer = unbegrenzt)"
                  data-testid="vehicle-included-km-input"
                />
                <p className="text-xs text-[#8A94A6] mt-1">Wird bei Buchung × Mietdauer auf Gesamt-Inklusivkilometer hochgerechnet.</p>
              </div>
              <div>
                <Label className="overline">€ je Mehrkilometer</Label>
                <Input
                  type="number"
                  value={formData.extra_km_rate}
                  onChange={(e) => setFormData({...formData, extra_km_rate: e.target.value})}
                  className="input-swiss"
                  min={0}
                  step={0.01}
                  placeholder="z. B. 0.30"
                  data-testid="vehicle-extra-km-rate-input"
                />
              </div>
            </div>

            <div className="pt-2 border-t border-[#E2E4E9]">
              <Label className="overline block mb-3">Fahrzeug-Identifikation</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="overline">Fahrgestellnummer (FIN/VIN)</Label>
                  <Input
                    value={formData.vin}
                    onChange={(e) => setFormData({...formData, vin: e.target.value.toUpperCase()})}
                    className="input-swiss"
                    placeholder="z. B. WVWZZZ1KZAW123456"
                    maxLength={17}
                    data-testid="vehicle-vin-input"
                  />
                </div>
                <div>
                  <Label className="overline">Erstzulassung</Label>
                  <Input
                    type="date"
                    value={formData.first_registration_date}
                    onChange={(e) => setFormData({...formData, first_registration_date: e.target.value})}
                    className="input-swiss"
                    data-testid="vehicle-first-registration-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="overline">Aktueller Kilometerstand</Label>
                  <Input
                    type="number"
                    value={formData.current_mileage}
                    onChange={(e) => setFormData({...formData, current_mileage: e.target.value})}
                    className="input-swiss"
                    min={0}
                    placeholder="z. B. 45000"
                    data-testid="vehicle-current-mileage-input"
                  />
                  <p className="text-xs text-[#8A94A6] mt-1">Wird nach jeder Übergabe/Rückgabe automatisch aktualisiert. Manuell jederzeit überschreibbar.</p>
                </div>
                <div>
                  <Label className="overline">Garantie-Ablauf</Label>
                  <Input
                    type="date"
                    value={formData.warranty_until}
                    onChange={(e) => setFormData({...formData, warranty_until: e.target.value})}
                    className="input-swiss"
                    data-testid="vehicle-warranty-until-input"
                  />
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-[#E2E4E9]">
              <Label className="overline block mb-3">TÜV / Hauptuntersuchung</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="overline">Nächste HU fällig am</Label>
                  <Input
                    type="date"
                    value={formData.tuev_date}
                    onChange={(e) => setFormData({...formData, tuev_date: e.target.value})}
                    className="input-swiss"
                    data-testid="vehicle-tuev-date-input"
                  />
                  <p className="text-xs text-[#8A94A6] mt-1">Wird automatisch ins Wartungscenter übernommen.</p>
                </div>
                <div>
                  <Label className="overline">Prüfstelle</Label>
                  <Input
                    value={formData.tuev_inspector}
                    onChange={(e) => setFormData({...formData, tuev_inspector: e.target.value})}
                    className="input-swiss"
                    placeholder="TÜV / DEKRA / GTÜ ..."
                    data-testid="vehicle-tuev-inspector-input"
                  />
                </div>
              </div>
              <div className="mt-4">
                <Label className="overline">Nächste Inspektion fällig am</Label>
                <Input
                  type="date"
                  value={formData.next_inspection_date}
                  onChange={(e) => setFormData({...formData, next_inspection_date: e.target.value})}
                  className="input-swiss"
                  data-testid="vehicle-next-inspection-input"
                />
                <p className="text-xs text-[#8A94A6] mt-1">Wird automatisch ins Wartungscenter übernommen.</p>
              </div>
            </div>

            <div className="pt-2 border-t border-[#E2E4E9]">
              <Label className="overline block mb-3">Versicherung</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="overline">Versicherungs­gesellschaft</Label>
                  <Input
                    value={formData.insurance_company}
                    onChange={(e) => setFormData({...formData, insurance_company: e.target.value})}
                    className="input-swiss"
                    placeholder="z. B. Allianz"
                    data-testid="vehicle-insurance-company-input"
                  />
                </div>
                <div>
                  <Label className="overline">Vertragsnummer</Label>
                  <Input
                    value={formData.insurance_policy_number}
                    onChange={(e) => setFormData({...formData, insurance_policy_number: e.target.value})}
                    className="input-swiss"
                    placeholder="z. B. VS-1234567"
                    data-testid="vehicle-insurance-policy-input"
                  />
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-[#E2E4E9]">
              <Label className="overline block mb-3">Ölwechsel</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="overline">Intervall (km)</Label>
                  <Input
                    type="number"
                    value={formData.oil_change_interval_km}
                    onChange={(e) => setFormData({...formData, oil_change_interval_km: e.target.value})}
                    className="input-swiss"
                    min={0}
                    placeholder="z. B. 15000"
                    data-testid="vehicle-oil-interval-km-input"
                  />
                </div>
                <div>
                  <Label className="overline">Intervall (Monate)</Label>
                  <Input
                    type="number"
                    value={formData.oil_change_interval_months}
                    onChange={(e) => setFormData({...formData, oil_change_interval_months: e.target.value})}
                    className="input-swiss"
                    min={0}
                    placeholder="z. B. 12"
                    data-testid="vehicle-oil-interval-months-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="overline">Letzter Ölwechsel (Datum)</Label>
                  <Input
                    type="date"
                    value={formData.last_oil_change_date}
                    onChange={(e) => setFormData({...formData, last_oil_change_date: e.target.value})}
                    className="input-swiss"
                    data-testid="vehicle-last-oil-date-input"
                  />
                </div>
                <div>
                  <Label className="overline">Letzter Ölwechsel (km)</Label>
                  <Input
                    type="number"
                    value={formData.last_oil_change_mileage}
                    onChange={(e) => setFormData({...formData, last_oil_change_mileage: e.target.value})}
                    className="input-swiss"
                    min={0}
                    data-testid="vehicle-last-oil-mileage-input"
                  />
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-[#E2E4E9]">
              <Label className="overline block mb-3">Service</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="overline">Service-Intervall (km)</Label>
                  <Input
                    type="number"
                    value={formData.service_interval_km}
                    onChange={(e) => setFormData({...formData, service_interval_km: e.target.value})}
                    className="input-swiss"
                    min={0}
                    placeholder="z. B. 30000"
                    data-testid="vehicle-service-interval-input"
                  />
                </div>
                <div>
                  <Label className="overline">Letzter Service (Datum)</Label>
                  <Input
                    type="date"
                    value={formData.last_service_date}
                    onChange={(e) => setFormData({...formData, last_service_date: e.target.value})}
                    className="input-swiss"
                    data-testid="vehicle-last-service-date-input"
                  />
                </div>
                <div>
                  <Label className="overline">Letzter Service (km)</Label>
                  <Input
                    type="number"
                    value={formData.last_service_mileage}
                    onChange={(e) => setFormData({...formData, last_service_mileage: e.target.value})}
                    className="input-swiss"
                    min={0}
                    data-testid="vehicle-last-service-mileage-input"
                  />
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-[#E2E4E9]">
              <Label className="overline block mb-3">Reifenwechsel</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="overline">Letzter Sommer­reifen­wechsel</Label>
                  <Input
                    type="date"
                    value={formData.last_tire_change_summer_date}
                    onChange={(e) => setFormData({...formData, last_tire_change_summer_date: e.target.value})}
                    className="input-swiss"
                    data-testid="vehicle-tire-summer-input"
                  />
                </div>
                <div>
                  <Label className="overline">Letzter Winter­reifen­wechsel</Label>
                  <Input
                    type="date"
                    value={formData.last_tire_change_winter_date}
                    onChange={(e) => setFormData({...formData, last_tire_change_winter_date: e.target.value})}
                    className="input-swiss"
                    data-testid="vehicle-tire-winter-input"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className="overline">Notizen</Label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="input-swiss w-full px-3 py-2 min-h-[80px]"
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                Abbrechen
              </Button>
              <Button type="submit" className="btn-primary" disabled={uploading} data-testid="save-vehicle-btn">
                {uploading ? 'Wird gespeichert…' : (editingVehicle ? 'Speichern' : 'Hinzufügen')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Vehicle Revenue Dialog */}
      <VehicleRevenueDialog
        open={!!revenueVehicle}
        onClose={() => setRevenueVehicle(null)}
        vehicleId={revenueVehicle?.id}
        vehicleLabel={revenueVehicle ? `${revenueVehicle.brand} ${revenueVehicle.model} (${revenueVehicle.license_plate})` : ''}
      />
    </div>
  );
};

export default Vehicles;
